/**
 * A manual journal (`/c/$companyId/journals/new` and `…/journals/$draftId`):
 * its header, lines and files. A draft saves as it stands, incomplete lines
 * included, and the server lists what stops it posting; Post posts it as
 * saved. Once posted the page is read-only and links to its entry.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Paperclip, Plus, X } from "lucide-react";
import { cmpMinor } from "@linkhr/accounting-domain";
import { downloadPresigned, qk } from "@linkhr/api-client/core";
import { useAuth } from "@linkhr/auth/react";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DateField,
  EmptyState,
  ErrorScreen,
  Icon,
  Input,
  PageHeader,
  Select,
  Skeleton,
  nonEmpty,
  useToast,
} from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { currencyDecimals, dateToIso, isoToDate, todayIso } from "@linkhr/i18n/format";
import {
  useAccounts,
  useCompanyBooks,
  useCompanyBranches,
  useContacts,
  useCostCentres,
  useJournalDraft,
  useJournals,
  usePeriods,
  type CompanyBooks,
  type Draft,
} from "../api/ledger";
import {
  useAttachFile,
  useCreateDraft,
  useDeleteDraft,
  useDetachFile,
  useFileUrl,
  usePostDraft,
  useSaveDraft,
} from "../api/journals";
import { StateChip } from "../components/StateChip";
import { overlayOpen } from "../components/keys";
import { CODES } from "../ledger/books";
import { useLedgerAccess } from "../ledger/useLedgerAccess";
import { useLedgerFormat } from "../ledger/format";
import { JournalGrid, JournalGridView, type GridRefs, type ViewLine } from "./JournalGrid";
import {
  blankLine,
  draftRequest,
  gridLines,
  gridTotals,
  isBlank,
  manualJournals,
  nextLineNo,
  postableAccounts,
  type DraftHeader,
  type GridLine,
} from "./lines";
import { ExceptionDialog, RefusalBanner, type ExceptionAsk } from "./RefusalBanner";
import { actionFor, readProblems, readRefusal, type Refusal } from "./refusal";

export function JournalEditor({
  companyId,
  draftId,
}: {
  companyId: string;
  /** Absent for a new journal. */
  draftId?: string;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const access = useLedgerAccess(companyId);
  const allowed = access.can(CODES.journal);
  const books = useCompanyBooks(companyId);
  const draft = useJournalDraft(companyId, allowed ? draftId : undefined);
  // A reload after someone else's save starts the form again from what they saved.
  const [revision, setRevision] = useState(0);
  if (!allowed) return <NoJournals />;
  const ready = books.data !== undefined && (draftId === undefined || draft.data !== undefined);
  if (books.data === undefined || !ready) {
    const error = books.error ?? draft.error;
    return error !== null ? (
      <ErrorScreen
        error={error}
        reset={() => {
          void books.refetch();
          if (draftId !== undefined) void draft.refetch();
        }}
      />
    ) : (
      <Skeleton height={420} aria-label={nonEmpty(t("accounting:loading"))} />
    );
  }
  return (
    <JournalForm
      key={`${draftId ?? "new"}:${revision}`}
      companyId={companyId}
      books={books.data}
      draft={draft.data}
      onReload={() => void draft.refetch().then(() => setRevision((r) => r + 1))}
    />
  );
}

/** The page for someone without the journal permission (the menu doesn't show it to them). */
export function NoJournals(): JSX.Element {
  const { t } = useTranslation("accounting");
  const heading = t("accounting:ledger.journal.no_access.title");
  return (
    <EmptyState
      entities={nonEmpty(heading)}
      nextAction={nonEmpty(heading)}
      heading={heading}
      body={t("accounting:ledger.journal.no_access.body")}
    />
  );
}

/** The reference lists a journal reads, and the accounts a line can take on `onDate`. */
function useGridRefs(companyId: string, onDate: string): GridRefs {
  const accounts = useAccounts(companyId).data ?? [];
  return {
    accounts,
    pickable: postableAccounts(accounts, onDate),
    contacts: useContacts(companyId).data ?? [],
    branches: useCompanyBranches(companyId).data ?? [],
    costCentres: useCostCentres(companyId).data ?? [],
  };
}

function JournalForm({
  companyId,
  books,
  draft,
  onReload,
}: {
  companyId: string;
  books: CompanyBooks;
  draft: Draft | undefined;
  onReload: () => void;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useAuth();
  const { toast } = useToast();
  const access = useLedgerAccess(companyId);
  const currency = books.functionalCurrency;
  const scale = currencyDecimals(currency);
  const today = todayIso(books.timeZone);

  const [start] = useState(() => {
    const header: DraftHeader = {
      journalId: draft?.journalId ?? "",
      accountingDate: draft?.accountingDate ?? today,
      reference: draft?.reference ?? "",
      description: draft?.description ?? "",
    };
    const lines = gridLines(draft?.lines);
    return {
      header,
      lines,
      sent: draft === undefined ? null : JSON.stringify(draftRequest(header, lines)),
    };
  });
  /** The draft as last answered by the server: version, problems, files, state. */
  const [base, setBase] = useState(draft);
  const [header, setHeader] = useState(start.header);
  const [lines, setLines] = useState<GridLine[]>(start.lines);
  /** The request last saved, to tell unsaved changes. */
  const [sent, setSent] = useState(start.sent);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [ask, setAsk] = useState<ExceptionAsk | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refs = useGridRefs(companyId, header.accountingDate);
  const journals = manualJournals(useJournals(companyId).data ?? []);
  const periods = usePeriods(companyId).data;
  const create = useCreateDraft(companyId);
  const save = useSaveDraft(companyId);
  const post = usePostDraft(companyId);
  const remove = useDeleteDraft(companyId);
  const busy = create.isPending || save.isPending || post.isPending || remove.isPending;

  const journalId = header.journalId !== "" ? header.journalId : (journals[0]?.id ?? "");
  const request = draftRequest({ ...header, journalId }, lines);
  const json = JSON.stringify(request);
  const dirty = json !== sent;
  const totals = gridTotals(lines, scale);
  const balanced = totals !== null && cmpMinor(totals.difference, "0", scale) === 0;
  const canSave = !busy && journalId !== "";
  const canPost = canSave && balanced && cmpMinor(totals.debit, "0", scale) > 0;
  // An owner's easy entry opens read-only: saving here would rewrite it as a manual journal.
  const editable =
    !access.readOnly && base?.state !== "POSTED" && (base === undefined || base.kind === "MANUAL");

  // The saved answer speaks for the saved date: after a new date, its entry-wide problems and
  // their banner wait for the next save (line problems stay where they stand).
  const sameDate = base?.accountingDate === header.accountingDate;
  const saved = readProblems(base?.problems).filter((p) => sameDate || p.lineNo !== null);
  const problems = refusal !== null && refusal.problems.length > 0 ? refusal.problems : saved;
  const action = refusal?.action ?? (sameDate ? actionFor(problems) : null);
  const period =
    periods?.periods.find((p) => p.from <= header.accountingDate && header.accountingDate <= p.to)
      ?.state ?? (base?.accountingDate === header.accountingDate ? base.period?.state : undefined);

  /** Saves what changed; the saved draft, or null when refused (the refusal shows). */
  const persist = async (): Promise<Draft | null> => {
    if (base !== undefined && !dirty) return base;
    try {
      const saved =
        base === undefined
          ? await create.mutateAsync(request)
          : await save.mutateAsync({ draftId: base.id, version: base.version, body: request });
      setBase(saved);
      setSent(json);
      setRefusal(null);
      return saved;
    } catch (error) {
      setRefusal(readRefusal(error));
      return null;
    }
  };

  /** Keeps the answer; a new draft then moves to its own address, already in the cache. */
  const settle = (answer: Draft): void => {
    setBase(answer);
    if (draft !== undefined) return;
    queryClient.setQueryData(
      qk.accounting.ledgerRead(tenantId ?? "", companyId, "draft", { draftId: answer.id }),
      answer,
    );
    void navigate({
      to: "/c/$companyId/journals/$draftId",
      params: { companyId, draftId: answer.id },
      replace: true,
    });
  };

  const onSave = async (): Promise<void> => {
    const saved = await persist();
    if (saved === null) return;
    toast({ description: t("accounting:ledger.journal.saved") });
    settle(saved);
  };

  /** Saves, then posts as saved; a refusal leaves the draft as saved, with its reasons. */
  const onPost = async (): Promise<void> => {
    const saved = await persist();
    if (saved === null) return;
    try {
      const posted = await post.mutateAsync({ draftId: saved.id });
      toast({
        variant: "success",
        description: t("accounting:ledger.journal.posted", { number: posted.number }),
      });
      settle(posted);
    } catch (error) {
      setRefusal(readRefusal(error));
      settle(saved);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (base === undefined) return;
    try {
      await remove.mutateAsync({ draftId: base.id });
      toast({ description: t("accounting:ledger.journal.deleted") });
      void navigate({ to: "/c/$companyId/journals", params: { companyId } });
    } catch (error) {
      setRefusal(readRefusal(error));
    } finally {
      setDeleting(false);
    }
  };

  const addLine = (): void => {
    const lineNo = nextLineNo(lines);
    setLines([...lines, blankLine(lineNo)]);
    // The new row's account picker takes the focus once it renders.
    window.setTimeout(
      () => document.querySelector<HTMLElement>(`[data-jline="${lineNo}"] button`)?.focus(),
      0,
    );
  };

  // Ctrl/⌘+S saves, Ctrl/⌘+Enter posts (physical keys, so any keyboard layout).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!editable || !(event.ctrlKey || event.metaKey) || overlayOpen()) return;
      if (event.code === "KeyS") {
        event.preventDefault();
        if (canSave) void onSave();
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (canPost) void onPost();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  if (!editable) {
    return base === undefined ? (
      <Alert variant="warning" description={t("accounting:ledger.journal.error.read_only")} />
    ) : (
      <DraftView companyId={companyId} draft={base} currency={currency} refs={refs} />
    );
  }

  return (
    <div className="flex flex-col gap-md pb-24">
      <PageHeader
        title={
          base === undefined
            ? t("accounting:ledger.journal.title_new")
            : t("accounting:ledger.journal.title_draft")
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-xs">
            <StateChip family="journal" value="DRAFT" />
            <span className="text-label text-muted">
              {t("accounting:ledger.journal.number_on_posting")}
            </span>
            {dirty && base !== undefined ? (
              <span className="text-label text-muted">
                {t("accounting:ledger.journal.unsaved")}
              </span>
            ) : null}
          </span>
        }
        actions={
          <>
            {base !== undefined ? (
              <Button variant="ghost" disabled={busy} onClick={() => setDeleting(true)}>
                {t("accounting:ledger.journal.delete")}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              disabled={!canSave}
              aria-keyshortcuts="Control+S Meta+S"
              onClick={() => void onSave()}
            >
              {t("accounting:ledger.journal.save")}
            </Button>
            <Button
              disabled={!canPost}
              aria-keyshortcuts="Control+Enter Meta+Enter"
              onClick={() => void onPost()}
            >
              {t("accounting:ledger.journal.post")}
            </Button>
          </>
        }
      />

      <Card density="compact" className="grid gap-md md:grid-cols-2 xl:grid-cols-4">
        <span className="flex flex-col gap-2xs text-label text-muted">
          {t("accounting:ledger.journal.fields.date")}
          <DateField
            aria-label={t("accounting:ledger.journal.fields.date")}
            {...(isoToDate(header.accountingDate) !== undefined
              ? { value: isoToDate(header.accountingDate) as Date }
              : {})}
            onChange={(date) => {
              if (date !== undefined) setHeader({ ...header, accountingDate: dateToIso(date) });
            }}
          />
          {period !== undefined ? (
            <span>
              <StateChip family="period" value={period} detail={f.month(header.accountingDate)} />
            </span>
          ) : null}
        </span>
        <label className="flex flex-col gap-2xs text-label text-muted">
          {t("accounting:ledger.journal.fields.journal")}
          <Select
            aria-label={t("accounting:ledger.journal.fields.journal")}
            placeholder={t("accounting:ledger.journal.choose_journal")}
            value={journalId}
            onValueChange={(next) => setHeader({ ...header, journalId: next })}
            options={journals.map((journal) => ({
              value: journal.id,
              label: f.name(journal.name),
            }))}
          />
        </label>
        <label className="flex flex-col gap-2xs text-label text-muted">
          {t("accounting:ledger.journal.fields.reference")}
          <Input
            value={header.reference}
            onChange={(event) => setHeader({ ...header, reference: event.target.value })}
          />
        </label>
        <label className="flex flex-col gap-2xs text-label text-muted">
          {t("accounting:ledger.journal.fields.description")}
          <Input
            value={header.description}
            onChange={(event) => setHeader({ ...header, description: event.target.value })}
          />
        </label>
      </Card>

      {base?.myLockException !== undefined && base.myLockException !== null ? (
        <Alert
          variant="info"
          description={t("accounting:ledger.journal.exception.held", {
            time: f.instant(base.myLockException.expiresAt, books.timeZone),
          })}
        />
      ) : null}

      <RefusalBanner
        companyId={companyId}
        action={action}
        problems={problems}
        currency={currency}
        args={refusal?.args ?? {}}
        onReload={onReload}
        onException={setAsk}
        onTryAgain={() => void onPost()}
        onUseDate={(date) => {
          setHeader({ ...header, accountingDate: date });
          setRefusal(null);
        }}
        fxHref={access.can(CODES.settings) ? `/c/${companyId}/settings/fx-rates` : null}
        rolesHref={access.can(CODES.settings) ? `/c/${companyId}/settings/default-accounts` : null}
      />

      <JournalGrid
        lines={lines}
        onLineChange={(line) =>
          setLines(lines.map((row) => (row.lineNo === line.lineNo ? line : row)))
        }
        onRemove={(lineNo) => setLines(lines.filter((row) => row.lineNo !== lineNo))}
        onLastEnter={addLine}
        refs={refs}
        currency={currency}
        totals={totals}
        problems={problems}
      />
      <span>
        <Button variant="secondary" size="sm" onClick={addLine}>
          <Icon as={Plus} size={14} />
          {t("accounting:journal.add_line")}
        </Button>
      </span>
      {/* The difference: never fixed by a line added for you. */}
      {totals !== null && lines.some((line) => line.debit !== "" || line.credit !== "") ? (
        <p
          aria-live="polite"
          className={balanced ? "text-body text-success" : "text-body text-danger"}
        >
          {balanced
            ? t("accounting:journal.balanced")
            : t("accounting:journal.out_of_balance", {
                amount: f.money(
                  totals.difference.startsWith("-")
                    ? totals.difference.slice(1)
                    : totals.difference,
                  currency,
                ),
              })}
        </p>
      ) : null}

      <DraftFiles companyId={companyId} draft={base} editable onChange={setBase} />

      <ExceptionDialog
        companyId={companyId}
        ask={ask}
        today={today}
        onClose={() => setAsk(null)}
        onOpened={() => void onPost()}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        variant="destructive"
        title={nonEmpty(t("accounting:ledger.journal.delete_confirm.title"))}
        action={nonEmpty(t("accounting:ledger.journal.delete"))}
        entity={nonEmpty(t("accounting:ledger.journal.delete_confirm.entity"))}
        dismissLabel={nonEmpty(t("accounting:ledger.journal.delete_confirm.keep"))}
        confirmDisabled={remove.isPending}
        onConfirm={() => void onDelete()}
      />
    </div>
  );
}

/** A posted draft, or any draft while the product is read-only: as saved, with a way to its entry. */
function DraftView({
  companyId,
  draft,
  currency,
  refs,
}: {
  companyId: string;
  draft: Draft;
  currency: string;
  refs: GridRefs;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  const access = useLedgerAccess(companyId);
  const journal = manualJournals(useJournals(companyId).data ?? []).find(
    (candidate) => candidate.id === draft.journalId,
  );
  const posted = draft.state === "POSTED";
  const nameOf = (list: readonly { id: string; name: Record<string, string> }[], id: string) =>
    f.name(list.find((item) => item.id === id)?.name);
  const lines: ViewLine[] = gridLines(draft.lines)
    .filter((line) => !isBlank(line))
    .map((line) => {
      const account = refs.accounts.find((candidate) => candidate.id === line.accountId);
      return {
        lineNo: line.lineNo,
        account: account !== undefined ? `${account.code} ${f.name(account.name)}` : "",
        description: line.description,
        contact: nameOf(refs.contacts, line.contactId),
        branch: nameOf(refs.branches, line.branchId),
        costCentre: nameOf(refs.costCentres, line.costCentreId),
        debit: line.debit,
        credit: line.credit,
      };
    });
  return (
    <div className="flex flex-col gap-md">
      <PageHeader
        title={
          posted
            ? t("accounting:ledger.journal.title_posted", { number: draft.number })
            : t("accounting:ledger.journal.title_draft")
        }
        subtitle={<StateChip family="journal" value={posted ? "POSTED" : "DRAFT"} />}
      />
      {posted ? (
        <Alert
          variant="info"
          description={
            <span className="flex flex-wrap items-center gap-sm">
              {t("accounting:ledger.journal.posted", { number: draft.number })}
              {access.can(CODES.report) ? (
                <Link
                  to="/c/$companyId/entries/$entryId"
                  params={{ companyId, entryId: draft.entryId }}
                  className="text-primary underline focus-visible:focus-ring"
                >
                  {t("accounting:ledger.journal.open_entry")}
                </Link>
              ) : null}
            </span>
          }
        />
      ) : null}
      <dl className="grid gap-md md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: t("accounting:ledger.journal.fields.date"),
            value: f.day(draft.accountingDate),
          },
          { label: t("accounting:ledger.journal.fields.journal"), value: f.name(journal?.name) },
          { label: t("accounting:ledger.journal.fields.reference"), value: draft.reference ?? "" },
          {
            label: t("accounting:ledger.journal.fields.description"),
            value: draft.description ?? "",
          },
        ].map(({ label, value }) => (
          <div key={label} className="flex flex-col gap-2xs">
            <dt className="text-label text-muted">{label}</dt>
            <dd className="text-body text-default">{value !== "" ? value : "—"}</dd>
          </div>
        ))}
      </dl>
      <JournalGridView lines={lines} totals={draft.totals} currency={currency} />
      <DraftFiles companyId={companyId} draft={draft} editable={false} onChange={() => undefined} />
    </div>
  );
}

/** The draft's files: download any; add or remove them while it is a draft. */
function DraftFiles({
  companyId,
  draft,
  editable,
  onChange,
}: {
  companyId: string;
  /** Undefined until the draft is first saved. */
  draft: Draft | undefined;
  editable: boolean;
  onChange: (draft: Draft) => void;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const { toast } = useToast();
  const attach = useAttachFile(companyId);
  const detach = useDetachFile(companyId);
  const fileUrl = useFileUrl(companyId);
  const picker = useRef<HTMLInputElement>(null);
  const files = draft?.attachments ?? [];
  const fail = (error: unknown): void => {
    const { action } = readRefusal(error);
    toast({
      variant: "danger",
      description:
        action.kind === "message" && action.key !== "accounting:ledger.journal.error.generic"
          ? t(action.key)
          : action.kind === "stale"
            ? t("accounting:ledger.journal.stale", {
                name: action.name ?? t("accounting:ledger.journal.someone"),
              })
            : t("accounting:ledger.journal.files.failed"),
    });
  };
  return (
    <section aria-labelledby="journal-files" className="flex flex-col gap-sm">
      <h2 id="journal-files" className="text-heading">
        {t("accounting:ledger.journal.files.title")}
      </h2>
      {draft === undefined ? (
        <p className="text-body text-muted">{t("accounting:ledger.journal.files.save_first")}</p>
      ) : (
        <>
          {files.length === 0 ? (
            <p className="text-body text-muted">{t("accounting:ledger.journal.files.none")}</p>
          ) : (
            <ul className="flex flex-col gap-2xs">
              {files.map((file) => (
                <li key={file.id} className="flex items-center gap-xs">
                  <Button
                    variant="link"
                    onClick={() =>
                      void downloadPresigned(
                        () => fileUrl(draft.id, file.id),
                        {
                          onFailure: () =>
                            toast({
                              variant: "danger",
                              description: t("accounting:ledger.journal.files.download_failed"),
                            }),
                        },
                        { fileName: file.fileName },
                      )
                    }
                  >
                    <bdi>{file.fileName}</bdi>
                  </Button>
                  {editable ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={detach.isPending}
                      aria-label={nonEmpty(
                        t("accounting:ledger.journal.files.remove", { name: file.fileName }),
                      )}
                      onClick={() =>
                        void detach
                          .mutateAsync({ draftId: draft.id, attachmentId: file.id })
                          .then(onChange, fail)
                      }
                    >
                      <Icon as={X} size={16} />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {editable ? (
            <span>
              <input
                ref={picker}
                type="file"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file !== undefined) {
                    void attach.mutateAsync({ draftId: draft.id, file }).then(onChange, fail);
                  }
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={attach.isPending}
                onClick={() => picker.current?.click()}
              >
                <Icon as={Paperclip} size={14} />
                {t("accounting:ledger.journal.files.add")}
              </Button>
            </span>
          ) : null}
        </>
      )}
    </section>
  );
}
